import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  Select,
  Td,
  Tr,
  Text,
  Tag,
  TagLabel,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { AddIcon, TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";
import { FiUpload } from "react-icons/fi";
import { FaClipboardList } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import { listNeeds } from "../../../api/needsApi";
import { listClients } from "../../../api/clientsApi";
import { listUsersInMyBusinessUnit } from "../../../api/clientsApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import CreateNeedWizardModal from "./CreateNeedWizardModal";
import ImportNeedsFromFileModal from "./ImportNeedsFromFileModal";
import NeedDetailModal from "./NeedDetailModal";
import { useTranslation } from "react-i18next";
import {
  NEED_STATUSES,
  NEED_STATUS_LABELS,
  NEED_STATUS_LABEL_KEYS,
  NEED_STATUS_COLORS,
  getStatusLabel,
} from "../../../constants/statuses";
import { ACQUISITION_CHANNEL_LABELS, CONTRACT_TYPE_LABELS } from "../../../constants/needs";
import {
  PageHeader,
  Panel,
  DataTable,
  StatusBadge,
  FilterBar,
  FilterSearch,
  EmptyState,
  TablePagination,
} from "../../../components/Portal/ui";

// Compact filter Select matching the template's 31px controls.
const filterSelectSx = {
  height: "31px !important",
  paddingTop: "0 !important",
  paddingBottom: "0 !important",
  lineHeight: "31px !important",
  fontSize: "12px",
  fontWeight: 600,
};

// Sortable column header rendered into DataTable's `columns[].label` slot.
const SortableHeader = ({ label, col, sortBy, sortDir, onSort, justify = "flex-start" }) => (
  <Box
    as="span"
    display="flex"
    w="100%"
    alignItems="center"
    justifyContent={justify}
    h="34px"
    mx="-12px"
    px="12px"
    gap="2px"
    cursor="pointer"
    userSelect="none"
    onClick={() => onSort(col)}
  >
    {label}
    {sortBy === col &&
      (sortDir === "asc" ? (
        <TriangleUpIcon boxSize="8px" />
      ) : (
        <TriangleDownIcon boxSize="8px" />
      ))}
  </Box>
);

const Needs = () => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedBusinessUnitId, isLoaded: isBUContextLoaded } = useBusinessUnit();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPagesFromApi, setTotalPagesFromApi] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [filterClientId, setFilterClientId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterStartDateFrom, setFilterStartDateFrom] = useState("");
  const [filterStartDateTo, setFilterStartDateTo] = useState("");
  const [filterEndDateFrom, setFilterEndDateFrom] = useState("");
  const [filterEndDateTo, setFilterEndDateTo] = useState("");
  const [filterSalesId, setFilterSalesId] = useState("");
  const [filterRecruiterId, setFilterRecruiterId] = useState("");
  const [filterHasMatches, setFilterHasMatches] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("");

  const [clients, setClients] = useState([]);
  const [buUsers, setBuUsers] = useState([]);

  // Modals
  const createModal = useDisclosure();
  const importFileModal = useDisclosure();
  const needDetailModal = useDisclosure();
  const [selectedNeedId, setSelectedNeedId] = useState(null);

  const handleCloseNeedModal = useCallback(() => {
    setSelectedNeedId(null);
    needDetailModal.onClose();
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("needId");
    nextParams.delete("need_id");
    setSearchParams(nextParams, { replace: true });
  }, [needDetailModal, searchParams, setSearchParams]);

  const fetchData = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!silent) {
      setLoading(true);
    }
    try {
      const params = {
        search: search || undefined,
        clientId: filterClientId || undefined,
        status: filterStatus || undefined,
        dateFrom: filterDateFrom || undefined,
        dateTo: filterDateTo || undefined,
        startDateFrom: filterStartDateFrom || undefined,
        startDateTo: filterStartDateTo || undefined,
        endDateFrom: filterEndDateFrom || undefined,
        endDateTo: filterEndDateTo || undefined,
        salesId: filterSalesId || undefined,
        recruiterId: filterRecruiterId || undefined,
        ...(filterHasMatches ? { hasMatches: filterHasMatches } : {}),
        ...(sortBy ? { sortBy } : {}),
        ...(sortDir ? { sortDir } : {}),
        page,
        pageSize
      };
      const res = await listNeeds(params, { buId: selectedBusinessUnitId || undefined });
      setItems(res.items || []);
      setTotalItems(res.totalItems || 0);
      setTotalPagesFromApi(typeof res.totalPages === "number" ? res.totalPages : null);
    } catch (e) {
      toast({ title: t("pilotNeeds.toasts.loadListFailed"), status: "error" });
      setItems([]);
      setTotalItems(0);
      setTotalPagesFromApi(null);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [
    search, filterClientId, filterStatus,
    filterDateFrom, filterDateTo, filterStartDateFrom, filterStartDateTo,
    filterEndDateFrom, filterEndDateTo, filterSalesId, filterRecruiterId,
    filterHasMatches, sortBy, sortDir,
    page, pageSize, selectedBusinessUnitId, toast, t
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refetch when Business Unit context changes (after BU context is ready)
  useEffect(() => {
    if (!isBUContextLoaded) return;
    setPage(1);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBUContextLoaded, selectedBusinessUnitId]);

  // Auto-refresh needs every 30 seconds
  useEffect(() => {
    if (!isBUContextLoaded) return;

    const intervalId = setInterval(() => {
      // Silent refresh so the table does not visibly reload
      fetchData({ silent: true });
    }, 30000);

    return () => clearInterval(intervalId);
  }, [fetchData, isBUContextLoaded]);

  useEffect(() => {
    // Fetch clients and BU users for filter dropdowns
    Promise.all([
      listClients({ page: 1, pageSize: 1000 }, { buId: selectedBusinessUnitId || undefined }),
      listUsersInMyBusinessUnit({ buId: selectedBusinessUnitId || undefined })
    ]).then(([clientsRes, usersRes]) => {
      setClients(clientsRes?.items || []);
      setBuUsers(usersRes || []);
    }).catch(() => {
      setClients([]);
      setBuUsers([]);
    });
  }, [selectedBusinessUnitId]);

  useEffect(() => {
    if (!isBUContextLoaded) return;
    const paramNeedId = searchParams.get("needId") || searchParams.get("need_id");
    if (!paramNeedId) return;
    if (String(selectedNeedId) === String(paramNeedId) && needDetailModal.isOpen) return;
    setSelectedNeedId(paramNeedId);
    needDetailModal.onOpen();
    if (searchParams.get("needId") !== paramNeedId) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("needId", paramNeedId);
      nextParams.delete("need_id");
      setSearchParams(nextParams, { replace: true });
    }
  }, [isBUContextLoaded, needDetailModal, searchParams, selectedNeedId, setSearchParams]);

  const onCreateSuccess = () => {
    setPage(1);
    setTimeout(() => {
      fetchData();
    }, 1000);
  };

  const handleView = (id) => {
    setSelectedNeedId(id);
    needDetailModal.onOpen();
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("needId", String(id));
    setSearchParams(nextParams, { replace: true });
  };

  const totalPages = useMemo(() => {
    return totalPagesFromApi || Math.max(1, Math.ceil(totalItems / pageSize));
  }, [totalPagesFromApi, totalItems, pageSize]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
    setPage(1);
  };

  const sortable = (label, col, justify) => (
    <SortableHeader
      label={label}
      col={col}
      sortBy={sortBy}
      sortDir={sortDir}
      onSort={handleSort}
      justify={justify}
    />
  );

  const columns = [
    { key: "date", label: sortable(t("pilotNeeds.list.table.date"), "created_at"), width: "9%" },
    { key: "title", label: sortable(t("pilotNeeds.list.table.title"), "title"), width: "14%" },
    { key: "client", label: sortable(t("pilotNeeds.list.table.client"), "client_name"), width: "11%" },
    { key: "skills", label: t("pilotNeeds.list.table.skills"), width: "16%" },
    { key: "status", label: sortable(t("pilotNeeds.list.table.status"), "status"), width: "11%" },
    { key: "acquisitionChannels", label: t("pilotNeeds.list.table.acquisitionChannels"), width: "10%" },
    { key: "contractTypes", label: t("pilotNeeds.list.table.contractTypes"), width: "10%" },
    { key: "sales", label: t("pilotNeeds.list.table.sales"), width: "9%" },
    { key: "recruiter", label: t("pilotNeeds.list.table.recruiter"), width: "10%" },
    { key: "daysOpen", label: sortable(t("pilotNeeds.list.table.daysOpen"), "created_at", "flex-end"), width: "8%", align: "right" },
  ];

  const parseSkills = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      } catch (_) {
        return [];
      }
    }
    return [];
  };

  const parseMulti = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter(Boolean);
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.filter(Boolean);
      } catch (_) {
        return [];
      }
    }
    return [];
  };

  const renderRow = (item) => {
    const pendingDeleteAt = item.pendingDeleteAt || item.pending_delete_at;
    const isPendingDelete = Boolean(pendingDeleteAt);
    // Check camelCase first (API response format), then fallback to snake_case
    const techSkills = parseSkills(item.skillsTechnical || item.skills_technical);
    const funcSkills = parseSkills(item.skillsFunctional || item.skills_functional);
    const businessSkills = parseSkills(item.skillsBusiness || item.skills_business);

    const acquisitionChannels = parseMulti(item.acquisitionChannels || item.acquisition_channels);
    const contractTypes = parseMulti(item.contractTypes || item.contract_types);
    const totalSkills = techSkills.length + funcSkills.length + businessSkills.length;

    return (
      <Tr
        key={item.id}
        onClick={() => handleView(item.id)}
        cursor="pointer"
        opacity={isPendingDelete ? 0.5 : 1}
      >
        <Td>{item.date || ""}</Td>
        <Td fontWeight="700" color="portal.text">{item.title}</Td>
        <Td>{item.clientName || ""}</Td>
        <Td sx={{ whiteSpace: "normal !important" }}>
          <HStack spacing="4px" wrap="wrap">
            {techSkills.slice(0, 3).map((skill, idx) => (
              <Tag key={`tech-${idx}`} size="sm" colorScheme="purple" variant="subtle" borderRadius="999px">
                <TagLabel fontSize="10px">{skill}</TagLabel>
              </Tag>
            ))}
            {funcSkills.slice(0, 3).map((skill, idx) => (
              <Tag key={`func-${idx}`} size="sm" colorScheme="orange" variant="subtle" borderRadius="999px">
                <TagLabel fontSize="10px">{skill}</TagLabel>
              </Tag>
            ))}
            {businessSkills.slice(0, 3).map((skill, idx) => (
              <Tag key={`biz-${idx}`} size="sm" colorScheme="green" variant="subtle" borderRadius="999px">
                <TagLabel fontSize="10px">{skill}</TagLabel>
              </Tag>
            ))}
            {totalSkills > 9 && (
              <Tag size="sm" colorScheme="gray" variant="subtle" borderRadius="999px">
                <TagLabel fontSize="10px">+{totalSkills - 9}</TagLabel>
              </Tag>
            )}
          </HStack>
        </Td>
        <Td>
          <StatusBadge
            label={getStatusLabel(t, NEED_STATUS_LABEL_KEYS, NEED_STATUS_LABELS, item.status)}
            colorScheme={NEED_STATUS_COLORS[item.status] || "gray"}
          />
        </Td>
        <Td sx={{ whiteSpace: "normal !important" }}>
          {acquisitionChannels.length === 0 ? (
            <Text color="portal.muted" sx={{ marginBottom: "0 !important" }}>-</Text>
          ) : (
            <HStack spacing="4px" wrap="wrap">
              {acquisitionChannels.slice(0, 2).map((v, idx) => (
                <Tag key={`acq-${idx}`} size="sm" colorScheme="blue" variant="subtle" borderRadius="999px">
                  <TagLabel fontSize="10px">{ACQUISITION_CHANNEL_LABELS[v] || v}</TagLabel>
                </Tag>
              ))}
              {acquisitionChannels.length > 2 && (
                <Tag size="sm" colorScheme="gray" variant="subtle" borderRadius="999px">
                  <TagLabel fontSize="10px">+{acquisitionChannels.length - 2}</TagLabel>
                </Tag>
              )}
            </HStack>
          )}
        </Td>
        <Td sx={{ whiteSpace: "normal !important" }}>
          {contractTypes.length === 0 ? (
            <Text color="portal.muted" sx={{ marginBottom: "0 !important" }}>-</Text>
          ) : (
            <HStack spacing="4px" wrap="wrap">
              {contractTypes.slice(0, 2).map((v, idx) => (
                <Tag key={`ct-${idx}`} size="sm" colorScheme="purple" variant="subtle" borderRadius="999px">
                  <TagLabel fontSize="10px">{CONTRACT_TYPE_LABELS[v] || v}</TagLabel>
                </Tag>
              ))}
              {contractTypes.length > 2 && (
                <Tag size="sm" colorScheme="gray" variant="subtle" borderRadius="999px">
                  <TagLabel fontSize="10px">+{contractTypes.length - 2}</TagLabel>
                </Tag>
              )}
            </HStack>
          )}
        </Td>
        <Td>{item.salesName || ""}</Td>
        <Td>
          {Array.isArray(item.recruiterNames) && item.recruiterNames.length > 0
            ? item.recruiterNames.join(", ")
            : item.recruiterName || ""}
        </Td>
        <Td textAlign="right">
          {item.daysOpen != null ? (
            <StatusBadge
              label={`${item.daysOpen}d`}
              colorScheme={item.daysOpen > 60 ? "red" : item.daysOpen > 30 ? "orange" : "green"}
            />
          ) : "-"}
        </Td>
      </Tr>
    );
  };

  return (
    // Bleed past PortalLayout's padding so the page-head spans the content edges.
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="CN"
        eyebrow={t("pilotNeeds.list.eyebrow", "ASAP PILOT")}
        title={t("pilotNeeds.list.pageTitle")}
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              h="32px"
              leftIcon={<Icon as={FiUpload} boxSize="13px" />}
              onClick={importFileModal.onOpen}
            >
              {t("pilotNeeds.list.actions.importFromFile")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              h="32px"
              leftIcon={<AddIcon boxSize="9px" />}
              onClick={createModal.onOpen}
            >
              {t("pilotNeeds.list.actions.create")}
            </Button>
          </>
        }
      />

      <Box p="20px">
        <Panel
          title={t("pilotNeeds.list.pageTitle")}
          count={
            loading
              ? t("pilotNeeds.list.summary.loading")
              : t("pilotNeeds.list.summary.total", { count: totalItems })
          }
          filters={
            <Box w="100%">
              {/* Row 1: search + dropdown filters */}
              <FilterBar>
                <FilterSearch
                  placeholder={t("pilotNeeds.list.filters.searchPlaceholder")}
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                />
                <Select
                  placeholder={t("pilotNeeds.list.filters.clientPlaceholder")}
                  value={filterClientId}
                  onChange={(e) => { setFilterClientId(e.target.value); setPage(1); }}
                  w="auto"
                  minW="160px"
                  sx={filterSelectSx}
                >
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Select>
                <Select
                  placeholder={t("pilotNeeds.list.filters.statusPlaceholder")}
                  value={filterStatus}
                  onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                  w="auto"
                  minW="160px"
                  sx={filterSelectSx}
                >
                  {NEED_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {getStatusLabel(t, NEED_STATUS_LABEL_KEYS, NEED_STATUS_LABELS, s.value)}
                    </option>
                  ))}
                </Select>
                <Select
                  placeholder={t("pilotNeeds.list.filters.salesPlaceholder")}
                  value={filterSalesId}
                  onChange={(e) => { setFilterSalesId(e.target.value); setPage(1); }}
                  w="auto"
                  minW="160px"
                  sx={filterSelectSx}
                >
                  {buUsers.map((u) => (
                    <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                  ))}
                </Select>
                <Select
                  placeholder={t("pilotNeeds.list.filters.recruiterPlaceholder")}
                  value={filterRecruiterId}
                  onChange={(e) => { setFilterRecruiterId(e.target.value); setPage(1); }}
                  w="auto"
                  minW="160px"
                  sx={filterSelectSx}
                >
                  {buUsers.map((u) => (
                    <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                  ))}
                </Select>
                <Select
                  placeholder={t("pilotNeeds.list.filters.hasMatchesPlaceholder")}
                  value={filterHasMatches}
                  onChange={(e) => { setFilterHasMatches(e.target.value); setPage(1); }}
                  w="auto"
                  minW="150px"
                  sx={filterSelectSx}
                >
                  <option value="">{t("pilotNeeds.list.filters.hasMatchesAny")}</option>
                  <option value="true">{t("pilotNeeds.list.filters.hasMatchesWith")}</option>
                  <option value="false">{t("pilotNeeds.list.filters.hasMatchesWithout")}</option>
                </Select>
              </FilterBar>

              {/* Row 2: date-range filters */}
              <Flex gap="16px" wrap="wrap" mt="10px">
                {[
                  {
                    label: t("pilotNeeds.list.filters.dateRangeLabel"),
                    from: filterDateFrom, setFrom: setFilterDateFrom,
                    to: filterDateTo, setTo: setFilterDateTo,
                  },
                  {
                    label: t("pilotNeeds.list.filters.startDateRangeLabel"),
                    from: filterStartDateFrom, setFrom: setFilterStartDateFrom,
                    to: filterStartDateTo, setTo: setFilterStartDateTo,
                  },
                  {
                    label: t("pilotNeeds.list.filters.endDateRangeLabel"),
                    from: filterEndDateFrom, setFrom: setFilterEndDateFrom,
                    to: filterEndDateTo, setTo: setFilterEndDateTo,
                  },
                ].map((grp) => (
                  <Box key={grp.label}>
                    <Text
                      textTransform="uppercase"
                      fontSize="10px"
                      fontWeight="800"
                      letterSpacing="0.055em"
                      color="portal.muted"
                      sx={{ marginBottom: "4px !important" }}
                    >
                      {grp.label}
                    </Text>
                    <HStack spacing="6px">
                      <Input
                        type="date"
                        value={grp.from}
                        onChange={(e) => { grp.setFrom(e.target.value); setPage(1); }}
                        h="31px"
                        w="150px"
                        fontSize="12px"
                        fontWeight="600"
                      />
                      <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                        {t("pilotNeeds.list.filters.to")}
                      </Text>
                      <Input
                        type="date"
                        value={grp.to}
                        onChange={(e) => { grp.setTo(e.target.value); setPage(1); }}
                        h="31px"
                        w="150px"
                        fontSize="12px"
                        fontWeight="600"
                      />
                    </HStack>
                  </Box>
                ))}
              </Flex>
            </Box>
          }
          footer={
            <TablePagination
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={(sz) => { setPageSize(sz); setPage(1); }}
            />
          }
        >
          <DataTable
            columns={columns}
            rows={items}
            loading={loading}
            renderRow={renderRow}
            emptyState={
              <EmptyState
                icon={<FaClipboardList />}
                title={t("pilotNeeds.list.table.noItems")}
                hint={t("pilotNeeds.list.emptyHint", "Create a need to start tracking client demand.")}
              />
            }
          />
        </Panel>
      </Box>

      <CreateNeedWizardModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        onSuccess={onCreateSuccess}
      />
      <ImportNeedsFromFileModal
        isOpen={importFileModal.isOpen}
        onClose={importFileModal.onClose}
        onSuccess={() => {
          setPage(1);
          fetchData();
        }}
      />
      <NeedDetailModal
        isOpen={needDetailModal.isOpen}
        onClose={handleCloseNeedModal}
        needId={selectedNeedId}
      />
    </Box>
  );
};

export default Needs;
